import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import {
  isoToDate,
  toISODate,
} from 'volto-ministerio-publico-federal/utils/brDate';
import {
  MONTHS,
  WEEKDAYS,
  DECADE,
  addDays,
  addMonths,
  buildMonthGrid,
  formatDayLabel,
  formatMonthLabel,
  isMonthOutOfRange,
  isOutOfRange,
  isSameDay,
  buildYearGrid,
  decadeStart,
  isYearOutOfRange,
  setYearMonth,
} from './monthGrid';

type View = 'days' | 'months' | 'years';

export interface CalendarPopoverProps {
  value: string;
  min?: string;
  max?: string;
  labelledBy?: string;
  onSelect: (iso: string) => void;
  onClose: () => void;
}

export const CalendarPopover: React.FC<CalendarPopoverProps> = ({
  value,
  min,
  max,
  labelledBy,
  onSelect,
  onClose,
}) => {
  const today = new Date();
  const selected = isoToDate(value);
  const [focused, setFocused] = useState<Date>(selected || today);
  const [view, setView] = useState<View>('days');
  const gridRef = useRef<HTMLDivElement>(null);
  // Só o dia focado entra na ordem de tabulação: dentro da grade quem navega
  // são as setas, e 42 paradas de Tab seriam intransponíveis.
  const shouldFocusRef = useRef(true);

  useEffect(() => {
    if (view !== 'days' || !shouldFocusRef.current) {
      return;
    }

    gridRef.current
      ?.querySelector<HTMLButtonElement>('[tabindex="0"]')
      ?.focus();
  }, [focused, view]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const move = (next: Date) => {
    shouldFocusRef.current = true;
    setFocused(next);
  };

  const year = focused.getFullYear();
  const decade = decadeStart(year);
  const month = new Date(year, focused.getMonth(), 1);

  const steps: Record<
    View,
    { back: Date; forward: Date; previous: string; next: string }
  > = {
    days: {
      back: addMonths(focused, -1),
      forward: addMonths(focused, 1),
      previous: 'Mês anterior',
      next: 'Próximo mês',
    },
    months: {
      back: setYearMonth(focused, year - 1, focused.getMonth()),
      forward: setYearMonth(focused, year + 1, focused.getMonth()),
      previous: 'Ano anterior',
      next: 'Próximo ano',
    },
    years: {
      back: setYearMonth(focused, year - DECADE, focused.getMonth()),
      forward: setYearMonth(focused, year + DECADE, focused.getMonth()),
      previous: 'Década anterior',
      next: 'Próxima década',
    },
  };

  const step = steps[view];

  const nextView: Record<View, View> = {
    days: 'months',
    months: 'years',
    years: 'months',
  };

  const headingActions: Record<View, string> = {
    days: 'Escolher mês',
    months: 'Escolher ano',
    years: 'Voltar para os meses',
  };

  const headings: Record<View, string> = {
    days: formatMonthLabel(month),
    months: `${year}`,
    years: `${decade} – ${decade + DECADE - 1}`,
  };

  const onDaysKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focused, -1),
      ArrowRight: () => addDays(focused, 1),
      ArrowUp: () => addDays(focused, -7),
      ArrowDown: () => addDays(focused, 7),
      Home: () => addDays(focused, -focused.getDay()),
      End: () => addDays(focused, 6 - focused.getDay()),
      PageUp: () => addMonths(focused, -1),
      PageDown: () => addMonths(focused, 1),
    };

    const next = moves[event.key];

    if (next) {
      event.preventDefault();
      move(next());
    }
  };

  return (
    <div
      className="mpf-datepicker__popover"
      role="dialog"
      aria-labelledby={labelledBy}
    >
      <div className="mpf-datepicker__nav">
        <button
          type="button"
          className="mpf-datepicker__nav-button"
          aria-label={step.previous}
          onClick={() => move(step.back)}
        >
          <CaretLeftIcon size={16} aria-hidden="true" />
        </button>
        {/* Atalho para sair da navegação mês a mês: o próprio título abre a
            grade de meses e, dela, a de anos. */}
        <button
          type="button"
          className="mpf-datepicker__heading"
          aria-live="polite"
          aria-label={`${headings[view]}. ${headingActions[view]}`}
          onClick={() => setView(nextView[view])}
        >
          {headings[view]}
        </button>
        <button
          type="button"
          className="mpf-datepicker__nav-button"
          aria-label={step.next}
          onClick={() => move(step.forward)}
        >
          <CaretRightIcon size={16} aria-hidden="true" />
        </button>
      </div>

      {view === 'days' && (
        <>
          <div className="mpf-datepicker__weekdays" aria-hidden="true">
            {WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday.long}-${index}`}>{weekday.short}</span>
            ))}
          </div>

          <div
            ref={gridRef}
            className="mpf-datepicker__grid"
            role="grid"
            tabIndex={-1}
            onKeyDown={onDaysKeyDown}
          >
            {buildMonthGrid(month).map((day) => {
              const disabled = isOutOfRange(day, min, max);
              const isSelected = Boolean(selected && isSameDay(day, selected));

              return (
                <button
                  key={toISODate(day)}
                  type="button"
                  role="gridcell"
                  tabIndex={isSameDay(day, focused) ? 0 : -1}
                  disabled={disabled}
                  aria-selected={isSelected}
                  aria-label={formatDayLabel(day)}
                  className={cn('mpf-datepicker__day', {
                    'mpf-datepicker__day--outside':
                      day.getMonth() !== month.getMonth(),
                    'mpf-datepicker__day--today': isSameDay(day, today),
                    'mpf-datepicker__day--selected': isSelected,
                  })}
                  onClick={() => {
                    shouldFocusRef.current = false;
                    onSelect(toISODate(day));
                  }}
                  onFocus={() => {
                    shouldFocusRef.current = false;
                    setFocused(day);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </>
      )}

      {view === 'months' && (
        <div className="mpf-datepicker__months">
          {MONTHS.map((name, index) => (
            <button
              key={name}
              type="button"
              className={cn('mpf-datepicker__cell', {
                'mpf-datepicker__cell--current':
                  index === today.getMonth() && year === today.getFullYear(),
                'mpf-datepicker__cell--selected':
                  Boolean(selected) &&
                  index === selected?.getMonth() &&
                  year === selected?.getFullYear(),
              })}
              disabled={isMonthOutOfRange(year, index, min, max)}
              aria-label={`${name} de ${year}`}
              onClick={() => {
                move(setYearMonth(focused, year, index));
                setView('days');
              }}
            >
              {name.slice(0, 3)}
            </button>
          ))}
        </div>
      )}

      {view === 'years' && (
        <div className="mpf-datepicker__months">
          {buildYearGrid(year).map((option) => {
            return (
              <button
                key={option}
                type="button"
                className={cn('mpf-datepicker__cell', {
                  'mpf-datepicker__cell--outside':
                    option < decade || option >= decade + DECADE,
                  'mpf-datepicker__cell--current':
                    option === today.getFullYear(),
                  'mpf-datepicker__cell--selected':
                    option === selected?.getFullYear(),
                })}
                disabled={isYearOutOfRange(option, min, max)}
                onClick={() => {
                  move(setYearMonth(focused, option, focused.getMonth()));
                  setView('months');
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarPopover;
